import { isSensitiveKey } from "@/lib/pii/sensitive-keys";

type LogLevel = "debug" | "info" | "warn" | "error";

const LEVEL_ORDER: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

// `typeof process` guards a runtime without `process` (Convex V8).
const isDev = (): boolean => typeof process !== "undefined" && process.env.NODE_ENV === "development";

/**
 * Context object accepted by `logger.{debug,info,warn,error}`. Values are
 * the things the logger knows how to render and redact: strings, numbers,
 * booleans, null/undefined, `Error` instances, nested contexts, and arrays
 * of any of the above. The array case is `readonly` so callers can hand in
 * a `readonly string[]` (a common shape) without having to copy first.
 *
 * Concrete typed objects without an index signature (e.g. `{ action: string;
 * reason: FilterDecisionReason }`) ARE assignable as a nested context
 * because they structurally satisfy `Record<string, LogContextValue>`
 * — every own property's type is a `LogContextValue`. If a caller hits an
 * "Index signature for type 'string' is missing" error here, the actual
 * fix is to wrap the value in `{ field: value }` rather than passing the
 * bare struct as the entire context.
 */
interface LogContext {
    [key: string]: LogContextValue;
}

type LogContextValue = string | number | boolean | null | undefined | Error | LogContext | readonly LogContextValue[];

const REDACTED = "[REDACTED]";
const MAX_STRING_LEN = 2048;

function isLogContext(v: LogContextValue): v is LogContext {
    return typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Error);
}

function redactPii(value: LogContextValue, depth = 0): LogContextValue {
    if (depth > 8) {
        return REDACTED;
    }
    if (value == null) {
        return value;
    }
    if (typeof value === "string") {
        return value.length > MAX_STRING_LEN ? "...[truncated]" : value;
    }
    if (value instanceof Error) {
        return { message: value.message, name: value.name };
    }
    if (Array.isArray(value)) {
        return value.map((v) => redactPii(v, depth + 1));
    }
    if (isLogContext(value)) {
        const out: LogContext = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = isSensitiveKey(k) ? REDACTED : redactPii(v, depth + 1);
        }
        return out;
    }
    return value;
}

function normalizeCtx(ctx: LogContext | Error | undefined): LogContext | undefined {
    if (ctx === undefined) {
        return undefined;
    }
    if (ctx instanceof Error) {
        const errorObj: LogContext = { name: ctx.name, message: ctx.message };
        if (ctx.stack) {
            errorObj.stack = ctx.stack;
        }
        return { error: errorObj };
    }
    return ctx;
}

interface Logger {
    debug(msg: string, ctx?: LogContext | Error): void;
    info(msg: string, ctx?: LogContext | Error): void;
    warn(msg: string, ctx?: LogContext | Error): void;
    error(msg: string, ctx?: LogContext | Error): void;
    child(defaultCtx: LogContext): Logger;
}

function formatDev(level: LogLevel, service: string, msg: string, ctx?: LogContext): string {
    const prefix = `[${service}] ${level.toUpperCase()}:`;
    const safeCtx = ctx ? (redactPii(ctx) as LogContext) : undefined;
    if (!safeCtx || Object.keys(safeCtx).length === 0) {
        return `${prefix} ${msg}`;
    }
    const ctxStr = Object.entries(safeCtx).map(([k, v]) => {
        if (v instanceof Error) {
            return `${k}=${v.message}`;
        }
        if (typeof v === "string") {
            return `${k}="${v}"`;
        }
        return `${k}=${JSON.stringify(v)}`;
    }).join(" ");
    return `${prefix} ${msg} { ${ctxStr} }`;
}

function formatJson(level: LogLevel, service: string, msg: string, ctx?: LogContext): string {
    const safeCtx = ctx ? (redactPii(ctx) as LogContext) : undefined;
    const entry: LogContext = { level, service, msg, ts: new Date().toISOString(), ...(safeCtx ?? {}) };
    return JSON.stringify(entry);
}

function createLogger(service: string, defaultCtx?: LogContext): Logger {
    const emit = (level: LogLevel, msg: string, ctx?: LogContext | Error) => {
        // The minimum level: "debug" in development, "info" elsewhere.
        if (LEVEL_ORDER[level] < LEVEL_ORDER[isDev() ? "debug" : "info"]) {
            return;
        }

        const normalizedCtx = normalizeCtx(ctx);
        const mergedCtx = defaultCtx || normalizedCtx ? { ...defaultCtx, ...normalizedCtx } : undefined;

        const formatted = isDev() ? formatDev(level, service, msg, mergedCtx) : formatJson(level, service, msg, mergedCtx);

        switch (level) {
            case "debug":
                {
                    console.debug(formatted);
                    break;
                }
            case "info":
                {
                    console.info(formatted);
                    break;
                }
            case "warn":
                {
                    console.warn(formatted);
                    break;
                }
            case "error":
                {
                    console.error(formatted);
                    break;
                }
        }
    };

    return { debug: (msg, ctx) => emit("debug", msg, ctx), info: (msg, ctx) => emit("info", msg, ctx), warn: (msg, ctx) => emit("warn", msg, ctx), error: (msg, ctx) => emit("error", msg, ctx), child: (childCtx) => createLogger(service, { ...defaultCtx, ...childCtx }) };
}

export function logger(service: string, defaultCtx?: LogContext): Logger {
    return createLogger(service, defaultCtx);
}

export type { LogContext, LogContextValue, LogLevel, Logger };
